import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { Check, Eye, EyeOff, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import logo from '@/assets/OKYC logo.webp';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { useRegister } from '../hooks/use-register';
import { useRequestRegistration } from '../hooks/use-request-registration';

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    label: 'Be at least 8 characters long',
    test: (password) => password.length >= 8,
  },
  {
    label: 'Be a mixture of letters and numbers',
    test: (password) => /[a-zA-Z]/.test(password) && /[0-9]/.test(password),
  },
  {
    label: 'Contain at least 1 upper case letter',
    test: (password) => /[A-Z]/.test(password),
  },
];

/**
 * All fields are declared required so that per-step validation via
 * `form.trigger([...subset])` enforces exactly the fields shown in that step.
 * Submission never goes through `form.handleSubmit` (which would validate the
 * whole schema, including fields not currently on screen).
 */
const RegisterFormSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters long'),
  referrerEmail: z.string().email('Invalid email address'),
  code: z.string().min(1, 'Access code is required'),
});

type RegisterFormValues = z.infer<typeof RegisterFormSchema>;

type Mode = 'request' | 'complete';

// Persist just enough to resume on refresh. Password and code are intentionally
// NOT persisted; the code step re-prompts for the password when it's missing.
const STORAGE_KEY = 'okyc_registration_progress';

interface PersistedProgress {
  mode: Mode;
  email: string;
  referrerEmail: string;
}

function readPersisted(): PersistedProgress | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PersistedProgress) : null;
  } catch {
    return null;
  }
}

interface RegisterFormProps {
  /** Prefill the prospect's email (e.g. from the approver email's resume link). */
  initialEmail?: string;
  /** Start directly on the code-entry step (e.g. arriving via the resume link). */
  startWithCode?: boolean;
}

export function RegisterForm({ initialEmail, startWithCode }: RegisterFormProps = {}) {
  const navigate = useNavigate();
  const { mutate: register, isPending: isRegistering } = useRegister();
  const requestRegistration = useRequestRegistration();
  const [showPassword, setShowPassword] = useState(false);

  // Read any persisted in-progress signup exactly once (not on every render).
  const [persisted] = useState(readPersisted);

  // Resolve the starting point from props (resume link) first, then any
  // persisted in-progress signup, otherwise the default request step.
  const [mode, setMode] = useState<Mode>(
    startWithCode || persisted?.mode === 'complete' ? 'complete' : 'request',
  );

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(RegisterFormSchema),
    defaultValues: {
      email: initialEmail ?? persisted?.email ?? '',
      password: '',
      referrerEmail: persisted?.referrerEmail ?? '',
      code: '',
    },
    mode: 'onChange',
  });

  const password = form.watch('password');
  const email = form.watch('email');
  const referrerEmail = form.watch('referrerEmail');

  // Persist resumable progress (never the password or code).
  useEffect(() => {
    const progress: PersistedProgress = { mode, email, referrerEmail };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    } catch {
      // sessionStorage unavailable (private mode / disabled) — non-fatal.
    }
  }, [mode, email, referrerEmail]);

  function clearPersisted() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  // Step 1: request an invite code, which is emailed to the nominated approver.
  async function handleRequestCode(event: React.FormEvent) {
    event.preventDefault();
    const valid = await form.trigger(['email', 'password', 'referrerEmail']);
    if (!valid) return;

    const values = form.getValues();
    requestRegistration.mutate(
      { email: values.email, referrerEmail: values.referrerEmail },
      {
        onSuccess: () => setMode('complete'),
      },
    );
  }

  // Step 2: complete registration with the code relayed by the approver.
  async function handleCompleteRegistration(event: React.FormEvent) {
    event.preventDefault();
    const valid = await form.trigger(['email', 'password', 'code']);
    if (!valid) return;

    const values = form.getValues();
    register(
      { email: values.email, password: values.password, code: values.code.trim() },
      {
        onSuccess: () => {
          clearPersisted();
          navigate({ to: '/account-setup' });
        },
      },
    );
  }

  return (
    <Card className="w-full max-w-md rounded-[5px] border border-[#D4DAE0]">
      <CardHeader className="space-y-4 text-center">
        <img src={logo} alt="OKYC Logo" className="mx-auto h-16 w-auto" />

        <div className="space-y-2">
          <h1 className="text-[20px] font-semibold leading-[120%] text-[#051923]">
            Create Your Account
          </h1>
          <p className="text-[16px] font-normal leading-[22px] text-[#051923]">
            Welcome to Optimal KYC.
          </p>
        </div>

        <p className="text-[16px] font-normal leading-[22px] text-[#051923]">
          Take Anti Money Laundering compliance to the next level with AI PEP & Sanctions Screening
          and automated Adverse Media Investigation.
        </p>
      </CardHeader>

      {/* A single form keeps the email + password fields mounted across both
          steps, so their values are never lost when switching between
          requesting a code and entering it. Only the approver/code fields and
          the action button swap by mode. */}
      <Form {...form}>
        <form onSubmit={mode === 'request' ? handleRequestCode : handleCompleteRegistration}>
          <CardContent className="space-y-4">
            {mode === 'complete' && (
              <p className="text-[16px] font-normal leading-[22px] text-[#051923]">
                {referrerEmail ? (
                  <>
                    We&apos;ve sent a one-time access code to{' '}
                    <span className="font-semibold">{referrerEmail}</span> for approval. Once they
                    share it with you, enter it below to finish creating your account.
                  </>
                ) : (
                  'Enter the email you signed up with, your password, and the access code your approver shared with you.'
                )}
              </p>
            )}

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="john.smith@company.com"
                      autoComplete="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="************"
                        autoComplete="new-password"
                        className="pr-10"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {mode === 'request' ? (
              <>
                {/* Password Requirements */}
                <div className="space-y-2">
                  <p className="text-[14px] font-medium text-[#051923]">Password must:</p>
                  <ul className="space-y-1">
                    {PASSWORD_REQUIREMENTS.map((requirement) => {
                      const isMet = requirement.test(password || '');
                      return (
                        <li key={requirement.label} className="flex items-center gap-2">
                          {isMet ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <X className="h-4 w-4 text-gray-400" />
                          )}
                          <span
                            className={`text-[14px] ${isMet ? 'text-green-600' : 'text-gray-500'}`}
                          >
                            {requirement.label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <FormField
                  control={form.control}
                  name="referrerEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Approver email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="approver@optimalkyc.com"
                          autoComplete="off"
                          {...field}
                        />
                      </FormControl>
                      <p className="text-[14px] text-gray-500">
                        Access is by approval only. Enter the email of your Optimal KYC approver — a
                        one-time code will be sent to them to share with you.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            ) : (
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Access code</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="Enter your access code"
                        autoComplete="one-time-code"
                        autoCapitalize="characters"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            {mode === 'request' ? (
              <>
                <Button
                  type="submit"
                  className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium leading-[100%] text-white transition-opacity duration-300 ease-out hover:bg-[#131313]/90"
                  disabled={requestRegistration.isPending}
                >
                  {requestRegistration.isPending ? 'Requesting code...' : 'Request access code'}
                </Button>

                <button
                  type="button"
                  onClick={() => setMode('complete')}
                  className="text-center text-[16px] font-normal leading-[22px] text-[#131313] underline"
                >
                  Already have an access code?
                </button>

                <p className="text-center text-[16px] font-normal leading-[22px] text-[#051923]">
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    className="font-semibold leading-[140%] text-[#131313] underline"
                  >
                    Login
                  </Link>
                </p>
              </>
            ) : (
              <>
                <Button
                  type="submit"
                  className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium leading-[100%] text-white transition-opacity duration-300 ease-out hover:bg-[#131313]/90"
                  disabled={isRegistering}
                >
                  {isRegistering ? 'Creating account...' : 'Create account'}
                </Button>

                <button
                  type="button"
                  onClick={() => setMode('request')}
                  className="text-center text-[16px] font-normal leading-[22px] text-[#131313] underline"
                >
                  Back
                </button>
              </>
            )}
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}
